"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasToJSON = exports.areEqual = exports.isEmpty = exports.isNotUndefined = exports.pickFields = exports.isQuery = exports.isInput = exports.initValue = exports.assertNever = exports.fail = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const is_plain_object_1 = require("is-plain-object");
const mobx_1 = require("mobx");
function fail(message) {
    throw new Error(message || "Failed");
}
exports.fail = fail;
function assertNever(x) {
    throw new Error("Unexpected object: " + x);
}
exports.assertNever = assertNever;
/** Introspects the `init` prop to see if has a `map` function/etc. and returns the form value. */
function initValue(config, init) {
    let value;
    if (isInput(init)) {
        value = init.input ? init.map(init.input) : init.ifUndefined;
    }
    else if (isQuery(init)) {
        value = init.query.data ? init.map(init.query.data) : init.ifUndefined;
    }
    else {
        value = init;
    }
    return pickFields(config, value !== null && value !== void 0 ? value : {});
}
exports.initValue = initValue;
function isInput(init) {
    return !!init && "input" in init && "map" in init;
}
exports.isInput = isInput;
function isQuery(init) {
    return !!init && "query" in init && "map" in init;
}
exports.isQuery = isQuery;
/**
 * Picks ony fields out of `instance` that are in the `formConfig`.
 *
 * This is useful for creating a form input from a GraphQL query result,
 * but ignoring extra data in the query that we don't want/need in the form.
 *
 * (Especially because the form.value will become our mutation's input type
 * that goes on the wire, we don't want superfluous data in it.)
 */
function pickFields(formConfig, instance) {
    // If the caller is using classes, i.e. with their own custom observable behavior, then just use as-is
    if (!is_plain_object_1.isPlainObject(instance)) {
        return instance;
    }
    return Object.fromEntries(Object.entries(formConfig).map(([key, _keyConfig]) => {
        const keyConfig = _keyConfig;
        const value = instance[key];
        if (keyConfig.type === "object") {
            if (value) {
                return [key, pickFields(keyConfig.config, value)];
            }
            else {
                return [key, value];
            }
        }
        else if (keyConfig.type === "list") {
            if (mobx_1.isObservable(value)) {
                // If we hit an observable array, leave it as the existing proxy so the our
                // ListFieldState will react to changes in the original array.
                return [key, value];
            }
            else if (value) {
                return [key, value.map((u) => pickFields(keyConfig.config, u))];
            }
            else {
                return [key, value];
            }
        }
        else if (keyConfig.type === "value") {
            return [key, value];
        }
        else {
            return assertNever(keyConfig);
        }
    }));
}
exports.pickFields = pickFields;
function isNotUndefined(value) {
    return value !== undefined;
}
exports.isNotUndefined = isNotUndefined;
function isEmpty(value) {
    return value === undefined || value === null || value === "";
}
exports.isEmpty = isEmpty;
/**
 * An equals that does deep-ish equality.
 *
 * We only do non-identity equals for:
 *
 * - "plain" objects that have no custom prototype/i.e. are object literals
 * - objects that implement `toJSON`
 * - arrays
 */
function areEqual(a, b, strictOrder) {
    if (is_plain_object_1.isPlainObject(a)) {
        return fast_deep_equal_1.default(mobx_1.toJS(a), mobx_1.toJS(b));
    }
    if (hasToJSON(a) || hasToJSON(b)) {
        const a1 = hasToJSON(a) ? a.toJSON() : a;
        const b1 = hasToJSON(b) ? b.toJSON() : b;
        return fast_deep_equal_1.default(a1, b1);
    }
    if (a && b && a instanceof Array && b instanceof Array) {
        if (strictOrder !== false) {
            return fast_deep_equal_1.default(a, b);
        }
        if (a.length !== b.length)
            return false;
        return a.every((a1) => b.some((b1) => areEqual(a1, b1)));
    }
    return a === b;
}
exports.areEqual = areEqual;
function hasToJSON(o) {
    return !!(o && typeof o === "object" && "toJSON" in o);
}
exports.hasToJSON = hasToJSON;
